import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { IFolderRecord } from "../../../lib/types";
import pb from "../../../lib/pocketbase";
import { createSlug } from "../../../lib/utils";
import BackButton from "../../form/BackButton";

interface FolderBreadcrumbProps {
    showHome?: boolean;
    folder: IFolderRecord;
    isPublic?: boolean;
}

interface Crumb {
    id: string;
    name: string;
}

export async function getFolderParents(folder: IFolderRecord): Promise<Crumb[]> {
    let crumb = [
        {
            id: folder.id,
            name: folder.name
        }
    ];

    async function recursiveGetParent(folder: IFolderRecord) {
        if (!folder.parent) return;
        try {
            const parent: IFolderRecord = await pb.collection("folder").getOne(folder.parent, {
                fields: "name,id,parent",
                expand: "parent"
            });
            crumb = [
                ...crumb,
                {
                    id: parent.id,
                    name: parent.name
                }
            ];
            await recursiveGetParent(parent);
        } catch (err) {
            return;
        }
    }

    await recursiveGetParent(folder);
    return crumb.reverse();
}

export default function FolderBreadcrumb({ folder, isPublic = false }: FolderBreadcrumbProps) {
    const [crumb, setCrumb] = useState<Crumb[]>([]);
    const navigate = useNavigate();

    async function load() {
        setCrumb(await getFolderParents(folder));
    }

    useEffect(() => {
        load();
    }, [folder]);

    return (
        <React.Fragment>
            <div className="hidden h-9 items-center gap-3 whitespace-nowrap rounded-lg bg-slate-100 px-4 text-slate-400 dark:bg-slate-800 dark:text-slate-500 lg:flex ">
                {isPublic && folder.expand.parent && <BackButton className="-ml-4 -mr-2" onClick={() => navigate(`/folder/${createSlug(folder.expand.parent.name)}/${folder.parent}`)} />}
                {!isPublic && <BackButton className="-ml-4 -mr-2" onClick={() => navigate(`/studio/notes/${folder.parent}`)} />}
                {!isPublic && (
                    <React.Fragment>
                        <Link to={`/studio/notes`}>
                            <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor" className="h-5 w-5">
                                <path
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    d="M2.25 12l8.954-8.955c.44-.439 1.152-.439 1.591 0L21.75 12M4.5 9.75v10.125c0 .621.504 1.125 1.125 1.125H9.75v-4.875c0-.621.504-1.125 1.125-1.125h2.25c.621 0 1.125.504 1.125 1.125V21h4.125c.621 0 1.125-.504 1.125-1.125V9.75M8.25 21h8.25"
                                />
                            </svg>
                        </Link>
                        <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={3} stroke="currentColor" className="h-3 w-3">
                            <path strokeLinecap="round" strokeLinejoin="round" d="M8.25 4.5l7.5 7.5-7.5 7.5" />
                        </svg>
                    </React.Fragment>
                )}
                {crumb.map((path, index) => (
                    <React.Fragment key={`bc-${path.id}`}>
                        <Link className={`${path.id === folder.id && "text-slate-600 dark:text-slate-300"}`} to={isPublic ? `/folder/${createSlug(path.name)}/${path.id}` : `/studio/notes/${path.id}`}>
                            {path.name}
                        </Link>
                        {path.id !== folder.id && (
                            <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={3} stroke="currentColor" className="h-3 w-3">
                                <path strokeLinecap="round" strokeLinejoin="round" d="M8.25 4.5l7.5 7.5-7.5 7.5" />
                            </svg>
                        )}
                    </React.Fragment>
                ))}
            </div>
            <div className="flex items-center gap-2 lg:hidden">
                {isPublic && folder.expand.parent && <BackButton onClick={() => navigate(`/folder/${createSlug(folder.expand.parent.name)}/${folder.parent}`)} />}
                {!isPublic && <BackButton onClick={() => navigate(`/studio/notes/${folder.parent}`)} />}
                <h2 className="text-xl font-bold ">{folder?.name || "Notes."}</h2>
            </div>
        </React.Fragment>
    );
}
